import { useEffect, useRef, useCallback, useState } from "react";
import { useToastStore, type Toast } from "@/stores/toastStore";

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ToastItem({ toast }: { toast: Toast }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const duration = toast.duration ?? 5000;
  const remainingRef = useRef(duration);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef(Date.now());
  const [paused, setPaused] = useState(false);

  const dismiss = useCallback(() => removeToast(toast.id), [toast.id, removeToast]);

  const startTimer = useCallback(() => {
    startRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startRef.current));
  }, []);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  function handleMouseEnter() {
    setPaused(true);
    pauseTimer();
  }

  function handleMouseLeave() {
    setPaused(false);
    startTimer();
  }

  return (
    <div
      className={`cx-toast cx-toast--${toast.type}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <span className="cx-toast-message">{toast.message}</span>
      <button
        onClick={dismiss}
        className="cx-msg-action cx-toast-close"
        aria-label="Dismiss"
      >
        <CloseIcon />
      </button>
      <div
        className="cx-toast-progress"
        style={{
          animationDuration: `${duration}ms`,
          animationPlayState: paused ? "paused" : "running",
        }}
      />
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="cx-toast-container" aria-live="polite" role="status">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
